import { X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import Button from '@/components/ui/Button';
import { userServiceApi } from '@/services/api';
import { useGoalModal } from '../contexts/GoalModalContext';
import { getMe } from '@/services/authService';

const toPersianDigits = (num: number | string) => String(num).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const getDailyGoalOptions = (goal: number): number[] => {
  if (goal < 60) {
    const baseOptions = [10, 15, 20, 30, 60];
    return baseOptions.filter(opt => opt > goal);
  }
  const roundedBase = Math.floor(goal / 10) * 10;
  return [roundedBase + 10, roundedBase + 25, roundedBase + 55];
};

const getStreakGoalOptions = (goal: number): number[] => {
  if (goal < 60) {
    const baseOptions = [6, 12, 24, 30, 60];
    return baseOptions.filter(opt => opt > goal);
  }
  const roundedBase = Math.floor(goal / 10) * 10;
  return [roundedBase + 10, roundedBase + 25, roundedBase + 55];
};

export const GoalModals = () => {
  const {
    showDailyGoalModal,
    showStreakGoalModal,
    closeDailyGoalModal,
    closeStreakGoalModal,
    emitGoalUpdate,
  } = useGoalModal();

  const [dailyGoal, setDailyGoal] = useState<number>(15);
  const [tempDailyGoal, setTempDailyGoal] = useState<number>(15);
  const [isSavingDailyGoal, setIsSavingDailyGoal] = useState(false);

  const [streakGoal, setStreakGoal] = useState<number>(30);
  const [tempStreakGoal, setTempStreakGoal] = useState<number>(30);
  const [isSavingStreakGoal, setIsSavingStreakGoal] = useState(false);

  // Load goals from profile
  useEffect(() => {
    const loadGoals = async () => {
      try {
        const profile = await getMe();
        if (typeof profile.daily_goal === 'number') {
          setDailyGoal(profile.daily_goal);
          setTempDailyGoal(profile.daily_goal);
        }
        if (typeof profile.streak_goal === 'number') {
          setStreakGoal(profile.streak_goal);
          setTempStreakGoal(profile.streak_goal);
        }
      } catch (e) {
        console.error('Failed to load goals', e);
      }
    };
    loadGoals();
  }, []);

  // Update temp values and reload goals when modals open
  useEffect(() => {
    if (showDailyGoalModal) {
      // Reload goal from server to get latest value
      (async () => {
        try {
          const profile = await getMe();
          if (typeof profile.daily_goal === 'number') {
            setDailyGoal(profile.daily_goal);
            setTempDailyGoal(profile.daily_goal);
          }
        } catch (e) {
          console.error('Failed to reload daily goal', e);
          setTempDailyGoal(dailyGoal);
        }
      })();
    }
  }, [showDailyGoalModal]);

  useEffect(() => {
    if (showStreakGoalModal) {
      // Reload goal from server to get latest value
      (async () => {
        try {
          const profile = await getMe();
          if (typeof profile.streak_goal === 'number') {
            setStreakGoal(profile.streak_goal);
            setTempStreakGoal(profile.streak_goal);
          }
        } catch (e) {
          console.error('Failed to reload streak goal', e);
          setTempStreakGoal(streakGoal);
        }
      })();
    }
  }, [showStreakGoalModal]);

  const handleSaveDailyGoal = async () => {
    try {
      setIsSavingDailyGoal(true);
      
      // Close immediately
      closeDailyGoalModal();
      
      const res = await userServiceApi.put<{ message: string; new_daily_goal: number }>('/auth/daily-goal', { daily_goal: tempDailyGoal });
      if (res.success && res.data) {
        setDailyGoal(res.data.new_daily_goal);
        // Emit event to update all components
        emitGoalUpdate();
      }
    } catch (error) {
      console.error('Failed to save daily goal', error);
    } finally {
      setIsSavingDailyGoal(false);
    }
  };

  const handleSaveStreakGoal = async () => {
    try {
      setIsSavingStreakGoal(true);
      
      // Close immediately
      closeStreakGoalModal();
      
      const res = await userServiceApi.put<{ message: string; new_streak_goal: number }>('/auth/streak-goal', { streak_goal: tempStreakGoal });
      if (res.success && res.data) {
        setStreakGoal(res.data.new_streak_goal);
        // Emit event to update all components
        emitGoalUpdate();
      }
    } catch (error) {
      console.error('Failed to save streak goal', error);
    } finally {
      setIsSavingStreakGoal(false);
    }
  };

  return (
    <>
      {/* Daily Goal Modal */}
      {showDailyGoalModal && (
        <div>
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[300]"
              onClick={closeDailyGoalModal}
            />
            <div
              className="fixed inset-0 z-[300] flex items-center justify-center p-4"
            >
              <div className={cn(
                'w-full max-w-sm bg-white rounded-2xl flex flex-col shadow-xl overflow-hidden'
              )} dir="rtl">
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground font-persian">هدف روزانه</h2>
                  <button onClick={closeDailyGoalModal} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="p-4">
                  <p className="mb-4 pr-1 text-right text-sm text-gray-500 font-persian">روزانه چند دقیقه می‌خواهید مطالعه کنید؟</p>
                  <div className={cn("grid gap-2", getDailyGoalOptions(dailyGoal).length <= 3 ? "grid-cols-3" : "grid-cols-5")}>
                    {getDailyGoalOptions(dailyGoal).map((minutes) => (
                      <button
                        key={minutes}
                        onClick={() => setTempDailyGoal(minutes)}
                        className={cn(
                          "py-3 rounded-xl border-2 transition-all font-persian text-sm font-semibold",
                          tempDailyGoal === minutes
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
                        )}
                      >
                        {toPersianDigits(minutes)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-center px-5 py-4 bg-white border-t border-gray-100">
                  <Button
                    variant="primary"
                    onClick={handleSaveDailyGoal}
                    disabled={isSavingDailyGoal || !getDailyGoalOptions(dailyGoal).includes(tempDailyGoal)}
                    className="!h-12 !w-52 !rounded-xl !border-primary-500 !bg-primary-500 px-3 text-sm font-bold shadow-md hover:!border-primary-600 hover:!bg-primary-600"
                  >
                    {isSavingDailyGoal ? 'در حال ذخیره...' : 'ثبت'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        </div>
      )}

      {/* Streak Goal Modal */}
      {showStreakGoalModal && (
        <div>
          <>
            <div
              className="fixed inset-0 bg-black/50 z-[300]"
              onClick={closeStreakGoalModal}
            />
            <div
              className="fixed inset-0 z-[300] flex items-center justify-center p-4"
            >
              <div className={cn(
                'w-full max-w-sm bg-white rounded-2xl flex flex-col shadow-xl overflow-hidden'
              )} dir="rtl">
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground font-persian">هدف پیوستگی</h2>
                  <button onClick={closeStreakGoalModal} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="p-4">
                  <p className="mb-4 pr-1 text-right text-sm text-gray-500 font-persian">هدف پیوستگی شما چند روز است؟</p>
                  <div className={cn("grid gap-2", getStreakGoalOptions(streakGoal).length <= 3 ? "grid-cols-3" : "grid-cols-5")}>
                    {getStreakGoalOptions(streakGoal).map((days) => (
                      <button
                        key={days}
                        onClick={() => setTempStreakGoal(days)}
                        className={cn(
                          "py-3 rounded-xl border-2 transition-all font-persian text-sm font-semibold",
                          tempStreakGoal === days
                            ? "border-orange-500 bg-orange-50 text-orange-700"
                            : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
                        )}
                      >
                        {toPersianDigits(days)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-center px-5 py-4 bg-white border-t border-gray-100">
                  <Button
                    variant="primary"
                    onClick={handleSaveStreakGoal}
                    disabled={isSavingStreakGoal || !getStreakGoalOptions(streakGoal).includes(tempStreakGoal)}
                    className="!h-12 !w-52 !rounded-xl !border-orange-500 !bg-orange-500 px-3 text-sm font-bold shadow-md hover:!border-orange-600 hover:!bg-orange-600"
                  >
                    {isSavingStreakGoal ? 'در حال ذخیره...' : 'ثبت'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        </div>
      )}
    </>
  );
};
